/**
 * POST /plan preference fields — mirrors iOS PlanCreateRequestBuilder +
 * DataExportService.exportCompactActivitiesMealsHotels / extractCompactPreferences.
 *
 * The planning API requires a non-empty `travelerPreferences` string.
 */

import type { PlanCreateRequest } from '../services/planningApi';
import type { TravelerPreferenceProfile } from '../services/userPreferencesSyncService';
import { settingsEn } from '../content/settings.en';
import {
  formattedActivityInterestNames,
  planAPIInterestIds,
} from './viatorInterestTaxonomy';

const SIGHTSEEING_STYLE_LABELS: Record<string, string> = {
  no_preference: 'No Preference',
  independent: 'Independent',
  self_guided: 'Self-Guided',
  small_group: 'Small Group',
  large_group: 'Large Group',
  private_guide: 'Private Guide',
};

const SIGHTSEEING_STYLES_OMITTED_FROM_API = new Set(['no_preference', 'independent']);

function cuisineBuckets(profile: TravelerPreferenceProfile): {
  loves: string[];
  likes: string[];
  avoids: string[];
} {
  const loves: string[] = [];
  const likes: string[] = [];
  const avoids: string[] = [];
  for (const [name, value] of Object.entries(profile.cuisinePreferences ?? {})) {
    if (value === 'love') loves.push(name);
    else if (value === 'avoid') avoids.push(name);
    else if (value === 'like') likes.push(name);
  }
  return { loves, likes, avoids };
}

function sightseeingDisplayName(style: string | null | undefined): string {
  const key = style?.trim() || 'no_preference';
  return SIGHTSEEING_STYLE_LABELS[key] ?? SIGHTSEEING_STYLE_LABELS.no_preference;
}

/** Structured sightseeingStyle for POST /plan; omitted for no_preference / independent. */
export function sightseeingStyleForPlanAPI(style: string | null | undefined): string | undefined {
  const key = style?.trim();
  if (!key || SIGHTSEEING_STYLES_OMITTED_FROM_API.has(key)) return undefined;
  return key;
}

/** Compact preference blob sent as `travelerPreferences`. */
export function exportTravelerPreferencesText(profile: TravelerPreferenceProfile): string {
  const lines: string[] = ['=== TRAVELER PREFERENCES ===', ''];

  const { loves, likes, avoids } = cuisineBuckets(profile);
  if (loves.length > 0 || likes.length > 0 || avoids.length > 0) {
    lines.push('Cuisine preferences:');
    lines.push(JSON.stringify({ loves, likes, avoids }));
    lines.push('');
  }

  if (profile.preferredAccommodationType) {
    lines.push(`Accommodation Type: ${profile.preferredAccommodationType}`);
  }

  if (profile.averageHotelBudget != null && Number.isFinite(profile.averageHotelBudget)) {
    lines.push(`Average Hotel Budget: $${Math.round(profile.averageHotelBudget)}`);
  }

  if ((profile.preferredActivities ?? []).length > 0) {
    const names = formattedActivityInterestNames(profile.preferredActivities);
    lines.push(`${settingsEn.travelerPrefs.activityInterests}: ${names}`);
  }

  lines.push(`${settingsEn.travelerPrefs.sightseeingStyle}: ${sightseeingDisplayName(profile.sightseeingStyle)}`);

  if ((profile.dietaryPreferences ?? []).length > 0) {
    lines.push(`Dietary Preferences: ${profile.dietaryPreferences.join(', ')}`);
  }

  if (profile.travelPace) {
    lines.push(`Travel Pace: ${profile.travelPace}`);
  }

  if (profile.typicalStayDuration != null && Number.isFinite(profile.typicalStayDuration)) {
    const nights = profile.typicalStayDuration;
    lines.push(`Typical Stay Duration: ${nights} night${nights === 1 ? '' : 's'}`);
  }

  if ((profile.preferredTimeSlots ?? []).length > 0) {
    lines.push(`Preferred Time Slots: ${profile.preferredTimeSlots.join(', ')}`);
  }

  const additional = profile.additionalPreferences?.trim();
  if (additional) {
    lines.push('');
    lines.push('Important Preferences to consider:');
    lines.push(additional);
  }

  if ((profile.extractedPatterns ?? []).length > 0) {
    lines.push('');
    lines.push('Extracted Patterns:');
    for (const pattern of profile.extractedPatterns) {
      lines.push(`- ${pattern}`);
    }
  }

  return `${lines.join('\n')}\n`;
}

/**
 * iOS always sends a non-empty compact export (activities / meals / hotels + prefs).
 * New web trips often have no items yet; keep the section headers so the API accepts the body.
 */
export function exportCompactPlanPreferences(profile: TravelerPreferenceProfile): string {
  return [
    '=== ACTIVITIES ===',
    '',
    'No activities found.',
    '',
    '=== MEALS ===',
    '',
    'No meals found.',
    '',
    '=== HOTELS ===',
    '',
    'No hotels found.',
    '',
    exportTravelerPreferencesText(profile).trimEnd(),
    '',
  ].join('\n');
}

export function buildPlanCreatePreferenceFields(
  profile: TravelerPreferenceProfile
): Pick<PlanCreateRequest, 'travelerPreferences' | 'preferredActivities' | 'sightseeingStyle'> {
  const fields: Pick<
    PlanCreateRequest,
    'travelerPreferences' | 'preferredActivities' | 'sightseeingStyle'
  > = {
    travelerPreferences: exportCompactPlanPreferences(profile),
    preferredActivities: planAPIInterestIds(profile.preferredActivities ?? []),
  };
  const style = sightseeingStyleForPlanAPI(profile.sightseeingStyle);
  if (style) fields.sightseeingStyle = style;
  return fields;
}
